import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'
import {
  revisionHasDocumentType,
  type EffectiveDocumentType,
} from '@constructionautomation/backend/convex/lib/revisionDocumentTypes'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/** Hides a row control until the row (`group`) is hovered; always shown on touch and focus. */
export const REVEAL_ON_ROW_HOVER =
  'focus-visible:opacity-100 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 [@media(hover:hover)]:focus:opacity-100'

/**
 * Format a timestamp to a human-readable relative time string.
 * Examples: "Just now", "2 minutes ago", "3 hours ago", "Yesterday", "Jan 15"
 */
export function formatRelativeTime(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp;
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) {
    return "Just now";
  }

  if (minutes < 60) {
    return minutes === 1 ? "1 minute ago" : `${minutes} minutes ago`;
  }

  if (hours < 24) {
    return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
  }

  if (days === 1) {
    return "Yesterday";
  }

  if (days < 7) {
    return `${days} days ago`;
  }

  // For older dates, show the date
  const date = new Date(timestamp);
  const month = date.toLocaleDateString("en-US", { month: "short" });
  const day = date.getDate();
  const year = date.getFullYear();
  const currentYear = new Date().getFullYear();

  if (year === currentYear) {
    return `${month} ${day}`;
  }

  return `${month} ${day}, ${year}`;
}

/**
 * Format a timestamp to a readable date string.
 * Example: "Jan 15, 2024"
 */
export function formatDate(timestamp: number): string {
  const date = new Date(timestamp);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export type DocumentTypeLabelValue = EffectiveDocumentType;
export { revisionHasDocumentType };

export function formatDocumentTypeLabel(value?: DocumentTypeLabelValue): string {
  if (!value) {
    return "Unclassified";
  }

  return value
    .split("_")
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ");
}
